/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {useIntl, defineMessage} from 'react-intl'
import {
    Flex,
    Text,
    Stack,
    VisuallyHidden
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {validatePassword} from '../../utils/password-utils'
import {Alert2Icon, SuccessIcon} from '../custom-icons'

const PASSWORD_REQUIREMENTS_MESSAGES = {
    EIGHT_LETTER_MINIMUM: defineMessage({
        id: 'password_requirements.error.eight_letter_minimum',
        defaultMessage: '8 characters minimum'
    }),
    ONE_UPPERCASE_LETTER: defineMessage({
        id: 'password_requirements.error.one_uppercase_letter',
        defaultMessage: '1 uppercase letter'
    }),
    ONE_LOWERCASE_LETTER: defineMessage({
        id: 'password_requirements.error.one_lowercase_letter',
        defaultMessage: '1 lowercase letter'
    }),
    ONE_NUMBER: defineMessage({
        id: 'password_requirements.error.one_number',
        defaultMessage: '1 number'
    }),
    // We use the 'values' prop to safely include literal curly braces in the message.
    // In react-intl, curly braces are used for placeholders, so to display them as part
    //  of the message, we define them as placeholders and pass the actual characters
    //  through the 'values' prop to avoid parse error.
    ONE_SPECIAL_CHARACTER: defineMessage({
        id: 'password_requirements.error.one_special_character',
        defaultMessage: '1 special character: # ! & $ % * + / ? = ^ ` ~ {closeBrace} | {openBrace}'
    })
}

/**
 * Generates the aria-describedby string of password requirements for password field.
 *
 * @param {string} formPrefix - The prefix to use for the IDs.
 * @returns {string} A space-separated string of IDs.
 */
export const generatePasswordAriaDescribedByIds = (formPrefix = '') => {
    return [
        `${formPrefix}password-requirements`, // password-requirements wrapper
        `${formPrefix}password-hasMinChars`,
        `${formPrefix}password-hasUppercase`,
        `${formPrefix}password-hasLowercase`,
        `${formPrefix}password-hasNumber`,
        `${formPrefix}password-hasSpecialChar`
    ].join(' ')
}

/**
 * UI component for styling password requirement line
 */
const PasswordRequirement = ({isValid, hintId, messageId, values}) => {
    const intl = useIntl()

    return (
        // ADA: period (.) at the end of each aria-label requirement
        <Flex align="center" gap={8} aria-label={intl.formatMessage(messageId, values) + '.'}>
            {isValid ? (
                <SuccessIcon
                    boxSize={4}
                    color="green"
                    aria-describedby={hintId}
                    aria-label={intl.formatMessage({
                        id: 'password_requirement.valid.rule',
                        defaultMessage: 'Valid rule'
                    })}
                />
            ) : (
                <Alert2Icon
                    boxSize={4}
                    color="red"
                    aria-describedby={hintId}
                    aria-label={intl.formatMessage({
                        id: 'password_requirement.invalid.rule',
                        defaultMessage: 'Invalid rule'
                    })}
                />
            )}
            <Text id={hintId} variant="bodySmall">
                {/* ADA: period (.) at the end of each requirement for screen readers */}
                {intl.formatMessage(messageId, values)}
                <VisuallyHidden>.</VisuallyHidden>
            </Text>
        </Flex>
    )
}

PasswordRequirement.propTypes = {
    /** Should it render in valid state */
    isValid: PropTypes.bool,

    hintId: PropTypes.string.isRequired,

    /** The message ID for aria-label */
    messageId: PropTypes.object.isRequired,

    /** Values for message placeholders */
    values: PropTypes.object
}

/**
 * Renders a list of password requirements. Each requirement line toggles to its `isValid`
 * state when the given password value meets the associated criteria.
 */
const PasswordRequirements = ({value, formPrefix = ''}) => {
    const pwValidations = validatePassword(value)
    const intl = useIntl()

    /**
     * Generates a summary of password validation requirements.
     *
     * @returns {string} A formatted message indicating the number of password
     * validation requirements that have been met out of the total number of
     * requirements.
     */
    const getValidationSummary = () => {
        const requirements = [
            pwValidations.hasMinChars,
            pwValidations.hasUppercase,
            pwValidations.hasLowercase,
            pwValidations.hasNumber,
            pwValidations.hasSpecialChar
        ]

        const totalRequirements = requirements.length
        const validRequirementsCount = requirements.filter((isValid) => isValid).length

        return intl.formatMessage(
            {
                id: 'password_requirements.progress',
                defaultMessage:
                    '{validRequirementsCount} out of {totalRequirements} requirements completed.'
            },
            {
                validRequirementsCount,
                totalRequirements
            }
        )
    }

    return (
        <Stack spacing={9} id={`${formPrefix}password-requirements`}>
            <VisuallyHidden>
                <output aria-live="polite">{getValidationSummary()}</output>
            </VisuallyHidden>

            <PasswordRequirement
                isValid={pwValidations.hasMinChars}
                hintId={`${formPrefix}password-hasMinChars`}
                messageId={PASSWORD_REQUIREMENTS_MESSAGES.EIGHT_LETTER_MINIMUM}
            />
            <PasswordRequirement
                isValid={pwValidations.hasUppercase}
                hintId={`${formPrefix}password-hasUppercase`}
                messageId={PASSWORD_REQUIREMENTS_MESSAGES.ONE_UPPERCASE_LETTER}
            />
            <PasswordRequirement
                isValid={pwValidations.hasLowercase}
                hintId={`${formPrefix}password-hasLowercase`}
                messageId={PASSWORD_REQUIREMENTS_MESSAGES.ONE_LOWERCASE_LETTER}
            />
            <PasswordRequirement
                isValid={pwValidations.hasNumber}
                hintId={`${formPrefix}password-hasNumber`}
                messageId={PASSWORD_REQUIREMENTS_MESSAGES.ONE_NUMBER}
            />
            <PasswordRequirement
                isValid={pwValidations.hasSpecialChar}
                hintId={`${formPrefix}password-hasSpecialChar`}
                messageId={PASSWORD_REQUIREMENTS_MESSAGES.ONE_SPECIAL_CHARACTER}
                values={{
                    openBrace: '{',
                    closeBrace: '}'
                }}
            />
        </Stack>
    )
}

PasswordRequirements.propTypes = {
    /** The password to check against */
    value: PropTypes.string,
    formPrefix: PropTypes.string
}

export default PasswordRequirements
